import { Component, inject, ViewChild, effect } from '@angular/core';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TagModule } from 'primeng/tag';
import { MenuModule } from 'primeng/menu';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { ContractingAuthorityService } from './contracting-authority.service';
import { Ac } from '@/types';
import { DataTableRowActions, DynamicDataTable } from '@/components/data-table-builder/data-table.types';
import { getContractingAuthorityDataTableStructure } from './utils/contracting-authority-data-table-object';
import { DataTableBuilderComponent } from '@/components/data-table-builder/data-table-builder.component';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { DialogObject } from '@/components/dialog/dialog.types';
import { ContractingAuthorityRepository } from '@/store/contracting-authority-state/contracting-authority-state.repository';
import { MessageService } from 'primeng/api';
import { getContractingAuthorityDeleteObject } from './utils/contracting-authority-delete-dialog-object';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { AuthorizationService } from '@/services/authorization.service';
import { UserRole } from '@/types/user.types';
import { createServerQuery } from '@/components/data-table-builder/server-query';

@Component({
  selector: 'sm-contracting-authority',
  imports: [
    PageHeaderComponent,
    CommonModule,
    ButtonModule,
    TableModule,
    IconFieldModule,
    InputIconModule,
    TagModule,
    MenuModule,
    InputTextModule,
    FormsModule,
    SelectModule,
    DataTableBuilderComponent,
    DialogComponent,
    TranslateModule,
  ],
  templateUrl: './contracting-authority.component.html',
  styleUrl: './contracting-authority.component.scss',
})
export class ContractingAuthorityComponent {
  //services
  contractingAuthorityService = inject(ContractingAuthorityService);
  contractingAuthorityRepository = inject(ContractingAuthorityRepository);
  router = inject(Router);
  messageService = inject(MessageService);
  translate = inject(TranslateService);
  authService = inject(AuthorizationService);

  //breadcrumb
  breadcrumb = [
    {
      label: 'settings.contractingAuthority.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.contractingAuthority.breadcrumb.authorities',
      routerLink: ['/settings', 'contracting-authority'],
    },
  ];

  //dialog
  @ViewChild('dialog') dialogComponent!: DialogComponent;
  contractingAuthorityDialog?: DialogObject;

  //row-actions
  rowActions: DataTableRowActions = {
    inspectAction: () => {
      this.router.navigate(['/settings', 'contracting-authority', 'details', this.contractingAuthorityRepository.getNested('response', 'id')]);
    },
    editAction: () => {
      this.router.navigate(['/settings', 'contracting-authority', 'update', this.contractingAuthorityRepository.getNested('response', 'id')]);
    },
    deleteAction: this.authService.hasRoles([UserRole.ADMIN_CENTRAL, UserRole.SUPER_ADMIN])
      ? () => {
          this.contractingAuthorityDialog = getContractingAuthorityDeleteObject({
            t: this.translate,
            store: this.contractingAuthorityRepository,
            representation: this.contractingAuthorityRepository.getNested('response', 'id'),
            action: () => {
              this.deleteContractingAuthority(this.contractingAuthorityRepository.getNested<number>('response', 'id'));
              this.dialogComponent.toggleVisible();
            },
            toggleVisiblity: () => {
              this.dialogComponent.toggleVisible();
            },
          });
          this.dialogComponent.toggleVisible();
        }
      : undefined,
    defaultActions: {
      edit: {
        label: 'table.actions.edit',
      },
      delete: {
        label: 'table.actions.delete',
      },
    },
  };
  // Server query for pagination
  serverQuery = createServerQuery({});

  //data
  data: Ac[] = [];
  totalRecords = 0;
  //data-table-object
  dataTableObject: DynamicDataTable = getContractingAuthorityDataTableStructure({
    store: this.contractingAuthorityRepository,
    rowActions: this.rowActions,
    createAction: this.authService.hasRoles([UserRole.ADMIN_CENTRAL, UserRole.SUPER_ADMIN])
      ? () => this.router.navigate(['/settings', 'contracting-authority', 'add'])
      : undefined,
    serverQuery: this.serverQuery,
  });
  loading = true;

  constructor() {
    effect(() => {
      this.serverQuery.page();
      this.serverQuery.pageSize();
      this.serverQuery.search();
      this.serverQuery.sortBy();
      this.serverQuery.sortOrder();
      this.loadData();
    });
  }

  loadData() {
    this.contractingAuthorityService
      .getContractingAuthorities({
        page: this.serverQuery.page(),
        size: this.serverQuery.pageSize(),
        sort: this.serverQuery.sortBy() ? `${this.serverQuery.sortBy()},${this.serverQuery.sortOrder()}` : '',
        search: this.serverQuery.search(),
      })
      .subscribe({
        next: data => {
          this.data = data.content;
          this.totalRecords = data.totalElements;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.contractingAuthority.ac.message.error.loading'),
          });
        },
      });
  }

  deleteContractingAuthority(id: number) {
    this.contractingAuthorityService.deleteContractingAuthority(id).subscribe({
      next: () => {
        this.loadData();
        this.contractingAuthorityRepository.reset();
        this.loading = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.contractingAuthority.ac.message.success.deleted'),
        });
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.contractingAuthority.ac.message.error.deleted'),
        });
      },
    });
  }
}
